import { Avatar, Button, Dropdown, Image, TableColumnsType, Tag } from "antd";
import Header from "../../../atoms/Header";
import TableUi from "../../../atoms/Table";
import DashboardTemplate from "../../../templates/Dashboard";
import { getInitials, useStableRandomColors } from "../../../config/utils";
import Filters from "./Components/Filters";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { CaretDownOutlined, MoreOutlined } from "@ant-design/icons";
import {
  CrossIcon,
  DeleteIcon,
  NeedHelp,
  TickIcon,
} from "../../../atoms/CustomIcons";
import { AxiosError } from "axios";
import ErrorMessage from "../../../atoms/Toasts/ErrorMessage";
import { PopUp } from "../../../atoms/PopUp";
import ConfirmDelete from "../../../atoms/ConfirmDelete";
import EmptyCard from "../../../atoms/EmptyCard";
import { UsersTableType } from "./ducks/types";
import { getUsersListing, removeUsersListingData } from "./ducks/actions";
import { changeUserStatus, deleteUser } from "./ducks/services";
import SuccessMessage from "../../../atoms/Toasts/SuccessMessage";
import dayjs from "dayjs";
import ChangePasswordPopUp from "../../MyProfile/Components/ChangePasswordPopUp";
import { PhoneInput } from "react-international-phone";
import { formatRelativeTime } from "../../../atoms/TimeFormatDisplay";

export default function Employees() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];
  const userListing = useAppSelector((state) => state?.team?.usersData);
  const myProfileData = useAppSelector(
    (state) => state?.myProfile?.userData?.data
  );
  const [pagination, setPagination] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableRecord, setTableRecord] = useState<UsersTableType | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isAllDeleteModalOpen, setIsAllDeleteModalOpen] = useState(false);
  const [isChangePswModalOpen, setIsChangePswModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeactivatedModalOpen, setIsDeactivatedModalOpen] = useState(false);
  const [isActivatedModalOpen, setIsActivatedModalOpen] = useState(false);
  const [deactivated, isDeactivated] = useState(false);
  const dropdownRef = useRef<HTMLButtonElement>(null);
  const avatarColor = useStableRandomColors(10);

  const [filters, setFilters] = useState<{
    search_text: string | undefined;
    deactivated: boolean | undefined;
    company: string | undefined;
    position: string | undefined;
    language: string | undefined;
    status: string | undefined;
  }>({
    search_text: undefined,
    deactivated: false,
    company: undefined,
    position: undefined,
    language: undefined,
    status: undefined,
  });

  const fetchUserListing = () => {
    const payload = {
      type: ["employee"],
      status: ["active", "invited"],
      offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
      limit: limit,
      company_id: [companyId],
    };
    dispatch(getUsersListing(payload));
  };

  const changeStatus = (val: string) => {
    if (val === "deactivated") {
      setIsDeactivatedModalOpen(true);
      return;
    }
    setLoading(true);
    functionChangeStatus(val);
  };

  const functionChangeStatus = (val: string) => {
    setLoading(true);
    const payload = {
      status: val,
    };
    changeUserStatus(tableRecord?.id || "", payload)
      .then(() => {
        setLoading(false);
        fetchUserListing();
        setIsDeleteModalOpen(false);
        setIsDeactivatedModalOpen(false);
        setIsActivatedModalOpen(false);
        isDeactivated(false);
        SuccessMessage(
          val === "deactivated" ? "Employee deactivated" : "Employee Activated"
        );
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const items = [
    {
      key: "1",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/edit-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/company-settings/edit-employee/${tableRecord?.id}/${companyId}`
            );
          }}
        >
          Edit
        </Button>
      ),
    },
    {
      key: "2",
      disabled:
        tableRecord?.status === "invited" ||
        tableRecord?.status === "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          icon={<Image src="/images/table/password-icon.svg" preview={false} />}
          onClick={(e) => {
            e.stopPropagation();
            setIsChangePswModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
          disabled={
            tableRecord?.status === "invited" ||
            tableRecord?.status === "deactivated"
          }
        >
          Change password
        </Button>
      ),
    },
    {
      key: "3",
      label: (
        <Button
          type="text"
          className="delete-hover"
          icon={<DeleteIcon />}
          onClick={(e) => {
            e.stopPropagation();
            setIsDeleteModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  const statusItems = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            setIsActivatedModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "deactivated",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            changeStatus("deactivated");
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Deactivated{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const statusItemsInvited = [
    {
      key: "active",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            setIsActivatedModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#00A07D", borderRadius: "2px" }}
          />{" "}
          Active{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
    {
      key: "inactive",
      label: (
        <Button
          type="text"
          className="no-hover"
          onClick={(e) => {
            e.stopPropagation();
            setIsDeactivatedModalOpen(true);
            if (dropdownRef.current) {
              dropdownRef?.current?.click(); // Simulate a click to close
            }
          }}
        >
          <Avatar
            shape="square"
            size={16}
            style={{ backgroundColor: "#6C7478", borderRadius: "2px" }}
          />{" "}
          Deactivated{" "}
          <TickIcon style={{ color: "#00A07D" }} className="status_checked" />
        </Button>
      ),
    },
  ];

  const columns: TableColumnsType<UsersTableType> = [
    {
      title: "Name",
      dataIndex: "first_name",
      width: 216,
      fixed: "left",
      sorter: (a, b) => a?.first_name?.localeCompare(b?.first_name),
      render: (text, record, index) => {
        return (
          <div className="text_with_avatar">
            {record?.image_url ? (
              <div className="participant_avatar">
                <Avatar src={record?.image_url} />
              </div>
            ) : (
              <div className="participant_avatar">
                <Avatar
                  style={{ backgroundColor: avatarColor?.[index] ?? "#ccc" }}
                >
                  {getInitials(record?.first_name, record?.last_name)}
                </Avatar>
              </div>
            )}
            <div className="text_wrap">
              {`${text} ${record?.last_name}`}
              {myProfileData?.id === record?.id && (
                <span style={{ color: "#98A3A9" }}> (me)</span>
              )}
              {record?.status === "invited" && (
                <span style={{ color: "#98A3A9" }}> (invited)</span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Position",
      dataIndex: "position_id",
      width: 155,
      render: (text) => {
        let positionName = "-";
        let tagClass = "default_tag";

        if (text === "1") {
          positionName = "Manager";
          tagClass = "blue_tag";
        } else if (text === "2") {
          positionName = "Crew leader";
          tagClass = "green_tag";
        } else if (text === "3") {
          positionName = "Technician";
          tagClass = "orange_tag";
        } else if (text === "4") {
          positionName = "Account manager";
          tagClass = "yellow_tag";
        } else if (text === "5") {
          positionName = "Production manager";
          tagClass = "violet_tag";
        } else if (text === "6") {
          positionName = "CEO";
          tagClass = "red_tag";
        }

        return positionName !== "-" ? (
          <Tag className={tagClass}>{positionName}</Tag>
        ) : (
          "-"
        );
      },
    },

    // {
    //   title: "Crew",
    //   dataIndex: "crew",
    //   width: 147,
    //   render: (text) => text || "-",
    // },
    {
      title: "Account email",
      dataIndex: "email",
      width: 250,
      render: (text) => text ?? "-",
    },
    {
      title: "Mobile phone",
      dataIndex: "phone",
      width: 173,
      render: (text) => (text ? <PhoneInput disabled value={text} /> : "-"),
    },
    {
      title: "Office phone",
      dataIndex: "phone",
      width: 173,
      render: (text) => (text ? <PhoneInput disabled value={text} /> : "-"),
    },
    {
      title: "Rockstar at",
      dataIndex: "rockstar_at",
      width: 147,
      render: (text) => text ?? "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 125,
      render: (text, record) =>
        myProfileData?.id === record?.id ? (
          <Tag
            style={{
              background: "#00A07D",
              color: " #fff",
              textTransform: "capitalize",
              border: 0,
            }}
          >
            {text}
          </Tag>
        ) : (
          <Dropdown
            menu={{
              items: text === "invited" ? statusItemsInvited : statusItems,
              selectable: true,
              selectedKeys: [text],
            }}
            trigger={["click"]}
            overlayClassName="status_dropdown_overlay"
          >
            <Button
              onClick={(e) => {
                setTableRecord(record);
                e.stopPropagation();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              className={`status_dropdown ${text}_status`}
              ref={dropdownRef}
            >
              {text === "active"
                ? "Active"
                : text === "invited"
                ? "Invited"
                : "Deactivated"}{" "}
              <div className="icon_arrow">
                <CaretDownOutlined />
              </div>
            </Button>
          </Dropdown>
        ),
    },
    {
      title: "First Language",
      dataIndex: "first_language",
      width: 147,
      render: (text) =>
        text ? <div style={{ textTransform: "capitalize" }}>{text}</div> : "-",
    },
    {
      title: "Joined company",
      dataIndex: "joined_date",
      width: 147,
      render: (text) => (text ? formatRelativeTime(text) : "-"),
    },
    {
      title: "Birthdate",
      dataIndex: "birthday",
      width: 147,
      render: (text) => (text ? dayjs(text).format("MMM D") : "-"),
    },
    {
      title: <Image src="/images/table/menu-burger.svg" preview={false} />,
      dataIndex: "action",
      width: 52,
      fixed: "right",
      render: (_text, record) => (
        <Dropdown
          menu={{
            items:
              myProfileData?.id === record?.id ? items?.slice(0, 3) : items,
          }}
          trigger={["click"]}
          placement="bottomRight"
          className="actionButton"
        >
          <Button
            onClick={(e) => {
              console.log("text", record);
              setTableRecord(record);
              e.stopPropagation();
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              padding: "6px",
              height: "auto",
              background: "transparent",
            }}
            className="only_icon"
            icon={<MoreOutlined />}
            ref={dropdownRef}
          />
        </Dropdown>
      ),
    },
  ];

  useEffect(() => {
    if (companyId) {
      const payload = {
        type: ["employee"],
        status: filters?.status ? [filters?.status] : ["active", "invited"],
        search_text: filters?.search_text,
        first_language: filters?.language ? [filters?.language] : undefined,
        offset: pagination > 1 ? limit * (pagination - 1) : pagination - 1,
        limit: limit,
        company_id: [companyId],
      };
      dispatch(getUsersListing(payload));
    }
  }, [dispatch, filters, pagination, limit, companyId]);

  const deleteFunction = () => {
    setLoading(true);
    deleteUser(
      selectedRowKeys?.length > 0 ? selectedRowKeys : [tableRecord?.id || ""]
    )
      .then(() => {
        setLoading(false);
        setIsDeleteModalOpen(false);
        setIsAllDeleteModalOpen(false);
        setSelectedRowKeys([]);
        fetchUserListing();
        SuccessMessage("User deleted");
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const confirmDeletePopUp = {
    visibility: isDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete
            <b>
              &nbsp;{tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?
            <br /> This action cannot be undone.
          </>
        }
        btnText="Delete employee"
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeleteModalOpen(false),
  };

  const confirmDeactivatedPopUp = {
    visibility: isDeactivatedModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsDeactivatedModalOpen(false)}
        heading="Deactivate user"
        paragraph={
          <>
            Are you sure you want to deactivate{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?<br /> This user won't be able to access their account anymore. The
            data associated with this user (e.g., work history) will be
            preserved.
          </>
        }
        btnText="Deactivate user"
        btnApi={() => functionChangeStatus("deactivated")}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsDeactivatedModalOpen(false),
  };

  const confirmActivatedPopUp = {
    visibility: isActivatedModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsActivatedModalOpen(false)}
        heading="Activate user"
        isArchivePopup={true}
        paragraph={
          <>
            Are you sure you want to deactivate{" "}
            <b>
              {tableRecord?.first_name} {tableRecord?.last_name}
            </b>
            ?<br /> This user won't be able to access their account anymore. The
            data associated with this user (e.g., work history) will be
            preserved.
          </>
        }
        btnText="Activate Account"
        btnApi={() => functionChangeStatus("active")}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsActivatedModalOpen(false),
  };

  useEffect(() => {
    dispatch(removeUsersListingData());
  }, [dispatch]);

  const deleteAllUsersPopUp = {
    visibility: isAllDeleteModalOpen,
    content: (
      <ConfirmDelete
        title=""
        onClose={() => setIsAllDeleteModalOpen(false)}
        heading="Confirm deletion"
        paragraph={
          <>
            Are you sure you want to delete selected employees? <br />
            This action cannot be undone.
          </>
        }
        btnText={`Delete employee${selectedRowKeys?.length > 1 ? "s" : ""}`}
        btnApi={deleteFunction}
        loading={loading}
      />
    ),
    width: 480,
    onCancel: () => setIsAllDeleteModalOpen(false),
  };

  const changePasswordPopup = {
    visibility: isChangePswModalOpen,
    content: (
      <ChangePasswordPopUp
        onClose={() => setIsChangePswModalOpen(false)}
        id={tableRecord?.id || ""}
      />
    ),
    width: 640,
    onCancel: () => setIsChangePswModalOpen(false),
  };

  return (
    <DashboardTemplate loading={userListing?.loader || loading}>
      <Header
        breadcrumb={[
          { label: "Company settings", href: `/company-settings/${companyId}` },
          { label: "Employees" },
        ]}
        title="Employees"
        description="Company employees who use LandScaling."
        btnText1="Need help?"
        btnIcon1={<NeedHelp />}
      />
      <TableUi<UsersTableType>
        columns={columns}
        data={{
          count: userListing?.count,
          data: userListing?.data || [],
        }}
        filters={
          <Filters
            setFilters={setFilters}
            filters={filters}
            deactivated={deactivated}
            isDeactivated={isDeactivated}
          />
        }
        pageSize={limit}
        setPageSize={setLimit}
        setCurrentPage={setPagination}
        currentPage={pagination}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        createButtonText="Add employee"
        createButtonLink={`/company-settings/add-employee/${companyId}`}
      />
      {userListing?.count === 0 && (
        <div className="empty_wrap">
          <EmptyCard
            imageHeight={220}
            title={
              deactivated
                ? "There are no deactivated employees."
                : filters?.search_text
                ? "No results match the filter or search criteria"
                : `There are no ${filters?.status ?? "active"} employees`
            }
            description={
              deactivated
                ? "Once the employee is deactivated, they will appear in this list."
                : filters?.search_text
                ? "Try adjusting the filters and search."
                : "Click the button below to add employees."
            }
            image={
              deactivated
                ? "/images/empty-icons/no-archived.svg"
                : "/images/empty-icons/no-active.svg"
            }
            btnText={filters?.search_text || deactivated ? "" : "Add employee"}
            btnLink={
              filters?.search_text || deactivated
                ? ""
                : `/company-settings/add-employee/${companyId}`
            }
          />
        </div>
      )}

      {selectedRowKeys?.length !== 0 ? (
        <div className="selection_bar" style={{ minWidth: "235px" }}>
          <div className="selected_count">
            <Button
              icon={<CrossIcon style={{ color: "white" }} />}
              className="only_icon"
              type="text"
              onClick={() => setSelectedRowKeys([])}
            />
            {selectedRowKeys.length}{" "}
            {`${selectedRowKeys.length === 1 ? "employee " : "employees "}`}
            selected
          </div>
          <div className="selection_buttons">
            <Button
              type="text"
              icon={<DeleteIcon />}
              onClick={() => {
                setIsAllDeleteModalOpen(true);
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      ) : null}

      <PopUp {...confirmDeletePopUp} />
      <PopUp {...deleteAllUsersPopUp} />
      <PopUp {...changePasswordPopup} />
      <PopUp {...confirmDeactivatedPopUp} />
      <PopUp {...confirmActivatedPopUp} />
    </DashboardTemplate>
  );
}
